import { type CatalogItem, type FindView, type ResolveResult, activeHunts, isMultiPart, itemPart, slotLabel } from '../models/types';
import { useState } from 'react';
import { ItemArt } from '../avatar/itemArt';
import { Bubble } from '../components/Bubble';
import { RarityBadge } from '../components/RarityBadge';
import { Sheet } from '../components/Sheet';
import { TimeLeftLabel } from '../components/TimeLeftLabel';
import { multiPartTipText } from '../models/multiPartTip';
import { usePlayer } from '../state/player';
import { usePreferences } from '../state/preferences';
import { isBusy, useScan } from '../state/scan';
import { colors, rarityColors } from '../theme';

/** Bottom sheet for a find: item art, name, rarity, description, Pick up / Leave behind. */
export function FindResultSheet({ find, item, onDone }: { find: FindView; item: CatalogItem; onDone: (result: ResolveResult | null) => void }) {
  const part = itemPart(item, find.partId);
  const busy = useScan((s) => isBusy(s));
  const color = rarityColors[find.rarity];
  // A placement part belongs to a live hunt; show how long that hunt has left.
  // Computed at render: the sheet is short-lived.
  const hunt = usePlayer((s) => (find.partId ? s.player?.hunts.find((h) => h.itemId === find.itemId) : undefined));
  const now = Math.floor(Date.now() / 1000);
  // How many of this item the player holds. A part counts against its assembled item.
  const owned = usePlayer((s) => s.player?.inventory.filter((i) => i.itemId === find.itemId).length ?? 0);

  // The multi-part helper card, decided once when the sheet opens. Only tapping "Got it"
  // records that the player has seen it. An unloaded player or an expired hunt counts as no hunt.
  const [tip, setTip] = useState<string | null>(() => {
    if (!isMultiPart(item) || usePreferences.getState().multiPartTipSeen) return null;
    const live = activeHunts(usePlayer.getState().player?.hunts ?? [], now).find((h) => h.itemId === find.itemId);
    return multiPartTipText(item.parts.length, live ? live.collected.length : null);
  });
  const dismissTip = () => {
    setTip(null);
    usePreferences.getState().markMultiPartTipSeen();
  };

  const resolve = async (pickup: boolean) => {
    const result = await useScan.getState().resolve({ pickup });
    onDone(result);
  };

  return (
    <Sheet dismissible={false} label="You found something">
      <h2 className="t-headline t-center">{part ? 'You found a part!' : 'You found something!'}</h2>
      <Bubble color={item.palette} border={`4px solid ${color}`} padding={24} style={{ marginTop: 16, textAlign: 'center' }} className="item-hero">
        <ItemArt item={item} className="emoji" />
        <div className="t-headline" style={{ fontWeight: 900, marginTop: 8 }}>{part?.name ?? item.name}</div>
        {part && <div style={{ color: 'rgba(43,45,66,0.7)' }}>part of {item.name}</div>}
        <div style={{ margin: '10px 0' }}><RarityBadge rarity={find.rarity} /></div>
        {hunt?.expiresAt != null && hunt.expiresAt > now && (
          <div style={{ marginBottom: 8 }}>
            <TimeLeftLabel expiresAt={hunt.expiresAt} now={now} />
          </div>
        )}
        {item.slot && <div className="t-label" style={{ marginTop: 8 }}>Wear it on: {slotLabel(item.slot)}</div>}
        <p style={{ marginTop: 14 }}>{item.description}</p>
        {isMultiPart(item) && <div className="t-label" style={{ marginTop: 10 }}>🧩 {item.parts.length} parts to assemble</div>}
      </Bubble>
      {owned > 0 && (
        <Bubble border={`3px solid ${colors.lemon}`} padding={16} style={{ marginTop: 16, textAlign: 'center' }} testId="already-have">
          🎒 {owned === 1 ? `You already have ${part ? `the ${item.name}` : 'this'}.` : `You already have ${owned} of ${part ? `the ${item.name}` : 'this'}.`} Leave it behind for another hunter to find, or pick it up to add another copy.
        </Bubble>
      )}
      {tip && (
        <Bubble border={`3px solid ${colors.sky}`} padding={16} style={{ marginTop: 16 }} testId="multi-part-tip">
          <div className="t-title-m">🧩 Multi-part items</div>
          <p style={{ margin: '6px 0 0' }}>{tip}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn text" onClick={dismissTip} data-testid="multi-part-tip-dismiss">
              Got it
            </button>
          </div>
        </Bubble>
      )}
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button type="button" className="btn outlined" style={{ flex: 2, padding: '18px 12px', whiteSpace: 'nowrap' }} disabled={busy} onClick={() => void resolve(false)}>
          Leave behind
        </button>
        <button type="button" className="btn filled" style={{ flex: 3, background: color }} disabled={busy} onClick={() => void resolve(true)}>
          {busy ? <span className="spinner" /> : '🤚'} Pick up
        </button>
      </div>
    </Sheet>
  );
}
